import { useState, useEffect } from "react";
import { listUsers, createUser, updateUser, resetUserPassword, deleteUser, getOrphanedUsers, deleteOrphan } from "../../lib/api";
import { Icon } from "../../components/icons";
import type { UserProfile } from "../../lib/types";

export function Users() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState<"user" | "super_admin">("user");
  const [error, setError] = useState<string | null>(null);
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [orphans, setOrphans] = useState<UserProfile[]>([]);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [showOrphans, setShowOrphans] = useState(false);
  const [orphanStats, setOrphanStats] = useState<{ cognitoCount: number; dynamoCount: number } | null>(null);

  // Only one action form at a time
  const clearAllForms = () => {
    setShowCreate(false);
    setNewEmail("");
    setNewPassword("");
    setNewRole("user");
    setResetUserId(null);
    setResetPassword("");
    setDeleteConfirmId(null);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      const result = await listUsers();
      setUsers(result.items);
    } catch {
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  async function loadOrphans() {
    setOrphanLoading(true);
    setError(null);
    try {
      const result = await getOrphanedUsers();
      setOrphans(result.orphans);
      setOrphanStats({ cognitoCount: result.cognitoCount, dynamoCount: result.dynamoCount });
      setShowOrphans(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to check orphans");
    } finally {
      setOrphanLoading(false);
    }
  }

  async function handleDeleteOrphan(userId: string) {
    setError(null);
    try {
      await deleteOrphan(userId);
      setOrphans((prev) => prev.filter((o) => o.userId !== userId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete orphan");
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await createUser({
        email: newEmail,
        role: newRole,
        ...(newPassword ? { password: newPassword } : {}),
      });
      setShowCreate(false);
      setNewEmail("");
      setNewPassword("");
      setNewRole("user");
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user");
    }
  };

  const handleToggleSuspend = async (user: UserProfile) => {
    const newStatus = user.status === "active" ? "suspended" : "active";
    try {
      await updateUser(user.userId, { status: newStatus });
      setUsers((prev) =>
        prev.map((u) =>
          u.userId === user.userId ? { ...u, status: newStatus } : u
        )
      );
    } catch {
      setError("Failed to update user");
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetUserId) return;
    setError(null);
    try {
      await resetUserPassword(resetUserId, resetPassword);
      setResetUserId(null);
      setResetPassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password");
    }
  };

  const handleDelete = async (userId: string) => {
    setError(null);
    try {
      await deleteUser(userId);
      setDeleteConfirmId(null);
      setUsers((prev) => prev.filter((u) => u.userId !== userId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete user");
      setDeleteConfirmId(null);
    }
  };

  const filtered = users.filter(
    (u) =>
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.userId.includes(search)
  );

  return (
    <div className="content space-y-4">
      <div className="ph">
        <div>
          <div className="ph-eyebrow">Admin · Users</div>
          <h1 className="ph-title">User accounts</h1>
          <div className="ph-lede">
            {users.length} user{users.length === 1 ? "" : "s"}. Create, reset passwords, change
            roles, or remove orphans.
          </div>
        </div>
        <div className="ph-actions">
          <button
            type="button"
            className={`btn ${showCreate ? "btn-line" : "btn-primary"}`}
            onClick={() => {
              if (showCreate) clearAllForms();
              else {
                clearAllForms();
                setShowCreate(true);
              }
            }}
          >
            {showCreate ? <><Icon.X /> Cancel</> : <><Icon.Plus /> Create user</>}
          </button>
        </div>
      </div>

      {error && <div className="err-banner">{error}</div>}

      {/* Create user form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="card space-y-3">
          <h3 className="text-sm font-medium text-slate-300">New User</h3>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="email@example.com"
            className="input"
            required
          />
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Password (min 8 chars, uppercase, lowercase, number)"
            className="input"
            required
            minLength={8}
          />
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as "user" | "super_admin")}
            className="input"
          >
            <option value="user">User</option>
            <option value="super_admin">Super Admin</option>
          </select>
          <div className="flex gap-2">
            <button type="button" onClick={clearAllForms} className="btn-ghost flex-1">
              Cancel
            </button>
            <button type="submit" className="btn-primary flex-1">
              Create
            </button>
          </div>
        </form>
      )}

      {/* Reset password modal */}
      {resetUserId && (
        <form onSubmit={handleResetPassword} className="card space-y-3">
          <h3 className="text-sm font-medium text-slate-300">
            Reset Password for {users.find((u) => u.userId === resetUserId)?.email}
          </h3>
          <input
            type="password"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            placeholder="New password (min 8 characters)"
            className="input"
            required
            minLength={8}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={clearAllForms}
              className="btn-ghost flex-1"
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary flex-1">
              Set Password
            </button>
          </div>
        </form>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmId && (
        <div className="card space-y-3 border-red-500/30">
          <h3 className="text-sm font-medium text-red-400">
            Delete user {users.find((u) => u.userId === deleteConfirmId)?.email}?
          </h3>
          <p className="text-xs text-slate-400">
            This will permanently remove the user from Cognito and DynamoDB.
            The user must be suspended and have zero job history.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={clearAllForms}
              className="btn-ghost flex-1"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDelete(deleteConfirmId)}
              className="flex-1 text-sm px-3 py-2 rounded-lg bg-red-500/20 text-red-400
                hover:bg-red-500/30 transition-colors"
            >
              Delete User
            </button>
          </div>
        </div>
      )}

      {/* Search + Orphan check */}
      <div className="flex gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email or ID…"
          className="input flex-1"
        />
        <button
          onClick={() => showOrphans ? setShowOrphans(false) : loadOrphans()}
          disabled={orphanLoading}
          className="btn-ghost text-xs px-3 whitespace-nowrap"
        >
          {orphanLoading ? "Checking…" : showOrphans ? "Hide Orphans" : "🔍 Find Orphans"}
        </button>
      </div>

      {/* Orphaned profiles */}
      {showOrphans && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-amber-400">
              Orphaned Profiles
              {orphanStats && (
                <span className="text-xs text-slate-500 ml-2">
                  ({orphanStats.dynamoCount} DynamoDB / {orphanStats.cognitoCount} Cognito)
                </span>
              )}
            </h3>
            {orphans.length > 0 && (
              <span className="text-xs text-amber-400/70">
                {orphans.length} orphan{orphans.length !== 1 ? "s" : ""} — in DynamoDB but not in Cognito
              </span>
            )}
          </div>
          {orphans.length === 0 ? (
            <div className="card py-4 text-center">
              <span className="text-sm text-emerald-400">✓ No orphaned profiles found</span>
            </div>
          ) : (
            orphans.map((orphan) => (
              <div key={orphan.userId} className="card flex items-center gap-3 border-amber-500/20">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200 truncate">
                    {orphan.email}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {orphan.userId} · created {orphan.createdAt ? new Date(orphan.createdAt).toLocaleDateString() : "unknown"}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteOrphan(orphan.userId)}
                  className="text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400
                    hover:bg-red-500/20 transition-colors whitespace-nowrap"
                >
                  Delete from DB
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* User list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse h-20" />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((user) => {
            const isAdmin = user.role === "super_admin";
            const isActive = user.status === "active";
            const initial = (user.email?.[0] ?? "?").toUpperCase();
            return (
              <div
                key={user.userId}
                className={`card flex items-center gap-3 p-3 ${
                  isAdmin ? "border-purple-500/20" : ""
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-semibold shrink-0 ${
                    isAdmin
                      ? "bg-purple-500/15 text-purple-300"
                      : "bg-slate-800 text-slate-300"
                  }`}
                >
                  {initial}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-100 truncate">
                    {user.email}
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        isAdmin
                          ? "bg-purple-500/15 text-purple-300"
                          : "bg-slate-800 text-slate-400"
                      }`}
                    >
                      {isAdmin ? "super admin" : "user"}
                    </span>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        isActive
                          ? "bg-emerald-500/15 text-emerald-300"
                          : "bg-red-500/15 text-red-300"
                      }`}
                    >
                      {user.status}
                    </span>
                    <span className="text-xs text-slate-500">
                      {user.jobCount} sample{user.jobCount === 1 ? "" : "s"}
                    </span>
                    <span className="text-xs text-slate-600 font-mono truncate">
                      {user.userId.slice(0, 8)}…
                    </span>
                  </div>
                </div>

                <div className="flex gap-1.5 shrink-0">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      clearAllForms();
                      setResetUserId(user.userId);
                    }}
                  >
                    Reset PW
                  </button>
                  <button
                    type="button"
                    className="btn btn-line"
                    onClick={() => handleToggleSuspend(user)}
                  >
                    {isActive ? "Suspend" : "Activate"}
                  </button>
                  {!isActive && user.jobCount === 0 && (
                    <button
                      type="button"
                      className="btn btn-ghost text-red-400"
                      onClick={() => {
                        clearAllForms();
                        setDeleteConfirmId(user.userId);
                      }}
                    >
                      <Icon.Trash /> Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">
              No users found
            </p>
          )}
        </div>
      )}
    </div>
  );
}
